import { DownloadOutlined, SaveOutlined } from "@ant-design/icons";
import {
  Table,
  Card,
  Select,
  Button,
  message,
  Form,
  InputNumber,
  Row,
  Col,
  Space,
  Tag,
  Typography,
  Avatar,
  Input,
} from "antd";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import useRequest from "../../hooks/useRequest";
import * as XLSX from "xlsx";

const ReferralInvitesManager = () => {
  const { request } = useRequest();
  const { Text } = Typography;
  const [period, setPeriod] = useState("monthly");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 300);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const referralColumns = [
    {
      title: "Referred By (Referrer)",
      dataIndex: "referrer",
      key: "referrer",
      render: (referrer) => (
        <div>
          <Text strong>{referrer?.name || "-"}</Text>
          <br />
          <Text type="secondary">{referrer?.email || "-"}</Text>
        </div>
      ),
    },
    {
      title: "Referrer Count",
      dataIndex: "referralCount",
      key: "referralCount",
      render: (referralCount) => (
        <div>
          <Text strong>{referralCount || 0}</Text>
        </div>
      ),
    },
    {
      title: "Referred To (Invitee)",
      dataIndex: "referred_user",
      key: "referred_user",
      render: (referred_user) => (
        <div>
          <Text strong>{referred_user?.name || "-"}</Text>
          <br />
          <Text type="secondary">{referred_user?.email || "-"}</Text>
        </div>
      ),
    },
    // {
    //   title: "Status",
    //   dataIndex: "status",
    //   key: "status",
    //   render: (status) => {
    //     let color = "default";
    //     if (status === "completed") color = "green";
    //     if (status === "pending") color = "gold";
    //     return <Tag color={color}>{status?.toUpperCase() || "N/A"}</Tag>;
    //   },
    // },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "date",
      render: (date) => moment(date).format("MMM DD, YYYY"),
    },
  ];

  const fetchData = (pagination = {}, filters) => {
    setLoading(true);
    const filterActive = filters ? filters.is_active : null;
    request({
      url: `${apiPath.referralInviteList}?status=${
        filterActive ? filterActive.join(",") : ""
      }&page=${pagination.current || 1}&pageSize=${
        pagination.pageSize || 10
      }&search=${debouncedSearchText}`,
      method: "GET",
      onSuccess: ({ data }) => {
        setList(data?.list?.docs || []);
        setPagination({
          current: data?.list?.page,
          total: data?.list?.totalDocs,
          pageSize: data?.list?.limit,
        });
        setLoading(false);
      },
      onError: (err) => {
        ShowToast(err, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchData();
  }, [
    refresh,
    debouncedSearchText,
    period,
    pagination.current,
    pagination.pageSize,
  ]);

  const onSearch = (e) => {
    setSearchText(e.target.value);
    setPagination((p) => ({ ...p, current: 1 }));
  };

  const onChangePeriod = (value) => {
    setPeriod(value);
    setPagination((p) => ({ ...p, current: 1 }));
  };

  const exportLeaderboardExcel = () => {
    if (list.length === 0) {
      message.warning("No data to export.");
      return;
    }

    const dataToExport = list.map((item) => ({
      "Referrer Name": item.referrer?.name || "Not Available",
      "Referrer Email": item.referrer?.email || "Not Available",
      "Invitee Name": item.referred_user?.name || "Not Available",
      "Invitee Email": item.referred_user?.email || "Not Available",
      Date: moment(item.created_at).format("MMM DD, YYYY"),
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Referrals");

    XLSX.writeFile(wb, "Referral_Invites.xlsx");
    message.success(
      "Data exported successfully! Please check the downloaded Excel file."
    );
  };

  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Card
        title="Referrers & Invite List"
        extra={
          <>
            <div className="w-100 d-flex align-items-baseline text-head_right_cont">
              <div className="pageHeadingSearch d-flex gap-2">
                <Input.Search
                  placeholder="Search by Referer Or Invitee"
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                  className="searchInput"
                />
                <Select
                  value={period}
                  style={{ width: 160 }}
                  onChange={onChangePeriod}
                  options={[
                    { value: "daily", label: "Daily" },
                    { value: "weekly", label: "Weekly" },
                    { value: "monthly", label: "Monthly" },
                    { value: "all", label: "All Time" },
                  ]}
                />
                <Button
                  className="primary_btn btnStyle"
                  icon={<DownloadOutlined />}
                  onClick={exportLeaderboardExcel}
                >
                  Export Excel
                </Button>
              </div>
            </div>
          </>
        }
      >
        <div className="table-responsive customPagination">
          <Table
            columns={referralColumns}
            dataSource={list}
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            bordered
            rowKey="_id"
          />
        </div>
      </Card>
    </div>
  );
};

export default ReferralInvitesManager;
